import React, { useEffect, useState, useCallback, ReactNode, useRef } from 'react';
import { TreeSelect, Spin } from 'antd';
import { debounce } from '../../utils/PerformanceUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import _ from 'lodash';
import { GLOBAL_DEBOUNCE_TIME } from '../../utils/DataConstants';

const { SHOW_PARENT } = TreeSelect;
const defaultPagination = { current: 1, pageSize: 1000 };

const arrDataToTreeData = (arrData, valueAccessor, labelAccessor) => {
	const mapData = {};
	let childIds = [];

	arrData.forEach((el) => {
		if (el && el.childrenIds && el.childrenIds.length) {
			childIds = [...childIds, ...el.childrenIds];
		}
		mapData[el.id] = el;
	});

	const transformRecord = (el) => ({
		value: el.id,
		key: el.id,
		title: labelAccessor(el),
		children: el.childrenIds ? el.childrenIds.map((id) => transformRecord(mapData[id])) : [],
	});

	return arrData.map((el) => transformRecord(el)).filter((rec) => !childIds.includes(rec.value));
};

const OWAsyncTreeSelect = (props) => {
	const {
		stateSlice,
		targetCollectionName,
		placeholder,
		disabledPlaceholder,
		valueAccessor = (el) => el.id,
		labelAccessor = (el) => el.name,
		additionalFilters,
		initialPagination,
		allowClear = false,
		size,
		style,
		mode,
		value,
		onChange,
		loading,
		disabled,
		emptyWhenDisabled,
		sortBy,
		fetchMultiple,
		renderRecord,
		fetchData,
	} = props;
	const [internalValue, setInternalValue] = useState(value);
	const [treeData, setTreeData] = useState([]);
	const [searchText, setSearchText] = useState('');
	const prevFiltersRef = useRef();

	// Sync props.value to internal state (controlled)
	useEffect(() => {
		if (value !== internalValue) {
			setInternalValue(value);
		}
	}, [value]);

	const labelFn = useCallback(
		(record = {}) => (labelAccessor ? labelAccessor(record) : record['name']),
		[labelAccessor]
	);

	const setTreeDataAfterFetch = useCallback(
		(arrData) => {
			const selectedVals = Array.isArray(internalValue) ? internalValue : [internalValue];
			const selectedRecords = selectedVals
				.map((v) => stateSlice.records[v])
				.filter((r) => r && valueAccessor(r));

			const dropdownRecords = arrData;
			const visibleRecords = [
				...selectedRecords,
				...dropdownRecords.filter((r) => !selectedVals.some((val) => valueAccessor(r) == val)),
			];

			const tree = arrDataToTreeData(visibleRecords, valueAccessor, labelFn);
			setTreeData(tree);
		},
		[internalValue, stateSlice, valueAccessor, labelFn]
	);

	useEffect(() => {
		const selectedVals = Array.isArray(value) ? value : [value];
		if (selectedVals.length > 0) {
			fetchMultiple(selectedVals, targetCollectionName);
		}
	}, []);

	useEffect(() => {
		if (
			!_.isEqual(prevFiltersRef.current, additionalFilters) ||
			prevFiltersRef.current === undefined
		) {
			prevFiltersRef.current = additionalFilters;
			fetchData(
				searchText.trim(),
				targetCollectionName,
				initialPagination || defaultPagination,
				sortBy,
				additionalFilters,
				false
			).then(setTreeDataAfterFetch);
		}
	}, [additionalFilters]);

	const debouncedSearch = useCallback(
		debounce((text) => {
			fetchData(
				text.trim(),
				targetCollectionName,
				initialPagination || defaultPagination,
				sortBy,
				additionalFilters,
				false
			).then(setTreeDataAfterFetch);
			setSearchText(text.trim());
		}, GLOBAL_DEBOUNCE_TIME),
		[fetchData, targetCollectionName, sortBy, additionalFilters]
	);

	const handleValueChange = useCallback(
		(values) => {
			const valueSet = new Set();
			const elementSet = new Set();

			function addAllChildren(array) {
				array.forEach(function f(a) {
					if (Array.isArray(a.children) && a.children.length) {
						a.children.forEach(f);
					} else {
						valueSet.add(a.key);
						elementSet.add(a);
					}
				});
			}

			function find(array, key) {
				let found;
				array.some(function f(a) {
					if (a.key === key) {
						found = a;
						return true;
					}
					if (Array.isArray(a.children) && a.children.length) {
						return a.children.some(f);
					}
				});
				return found;
			}

			if (mode === 'multiple') {
				values.forEach((key) => {
					const entity = find(treeData, key);
					if (entity && entity.children && entity.children.length) {
						addAllChildren(entity.children);
					} else if (entity) {
						valueSet.add(entity.key);
						elementSet.add(entity);
					}
				});
			} else {
				const entity = find(treeData, values);
				if (entity) {
					elementSet.add(entity);
				}
			}

			const updatedValue = mode === 'multiple' ? Array.from(valueSet) : values;
			const updatedElement =
				mode === 'multiple' ? Array.from(elementSet) : Array.from(elementSet)[0];

			if (onChange) onChange(updatedValue, updatedElement);
			if (!('value' in props)) {
				setInternalValue(updatedValue);
			}
		},
		[mode, treeData, onChange]
	);

	const fetching = stateSlice.fetching;
	const noVisibleRecordsWithoutSearch =
		nullSafeGetOrElse('length', searchText, 0) === 0 && treeData.length === 0;

	const renderingData = disabled && emptyWhenDisabled ? [] : treeData;

	return (
		<TreeSelect
			multiple={mode === 'multiple'}
			treeCheckable={mode === 'multiple'}
			showCheckedStrategy={SHOW_PARENT}
			treeDefaultExpandAll
			notFoundContent={fetching ? <Spin size="small" /> : null}
			placeholder={disabled || noVisibleRecordsWithoutSearch ? disabledPlaceholder : placeholder}
			loading={loading}
			showSearch
			filterTreeNode={false}
			treeNodeFilterProp="title"
			onChange={handleValueChange}
			treeData={renderingData}
			size={size}
			style={style}
			allowClear={allowClear}
			value={disabled && emptyWhenDisabled ? [] : internalValue}
			disabled={disabled || noVisibleRecordsWithoutSearch}
			onSearch={debouncedSearch}
		/>
	);
};

export default OWAsyncTreeSelect;
